import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import { apiFetch } from '../../../lib/api';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import ModaleCercaRecord from '../../../components/ModaleCercaRecord';
import TabDashboard from './components/TabDashboard';
import TabAnagrafica from './components/TabAnagrafica';
import TabCliente from './components/TabCliente';
import TabFornitore from './components/TabFornitore';
import TabDocumenti from './components/TabDocumenti';
import TabLog from './components/TabLog';

// ── Interfaces ────────────────────────────────────────────────────────────
interface Nominativo {
  id: number;
  codice: string;
  tipoSoggetto: 'AZIENDA' | 'PRIVATO';
  ragioneSociale?: string;
  nome?: string;
  cognome?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  codiceSDI?: string;
  pec?: string;
  telefono?: string;
  emailPrincipale?: string;
  sito?: string;
  note?: string;
  isCliente: boolean;
  isFornitore: boolean;
  disattivato: boolean;
  bloccato: boolean;
  dataDisattivazione?: string;
  utenteDisattivazione?: string;
  societa?: {
    id: number;
    nome: string;
  };
  cliente?: {
    categoriaCliente: string;
    accountId?: number;
    insegnaClienteId?: number;
    insegnaCliente?: { id: number; nome: string; codice: string };
    codiceIvaDefaultId?: number;
    codiceIvaDefault?: { id: number; codice: string; descrizione: string; aliquota: number };
    note?: string;
  };
  fornitore?: {
    categoriaFornitore: string;
    iban?: string;
    banca?: string;
    filialeBanca?: string;
    codiceIvaDefaultId?: number;
    codiceIvaDefault?: { id: number; codice: string; descrizione: string; aliquota: number };
    note?: string;
  };
  indirizzi?: Array<{
    id: number;
    tipo: 'SEDE_LEGALE' | 'SEDE_OPERATIVA' | 'SPEDIZIONE';
    nome?: string;
    via?: string;
    cap?: string;
    citta?: string;
    provincia?: string;
    paese?: string;
    principale: boolean;
  }>;
  email?: Array<{
    id: number;
    etichetta: string;
    email: string;
    principale: boolean;
    ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
  }>;
  contatti?: Array<{
    id: number;
    nome: string;
    cognome?: string;
    ruolo?: string;
    telefono?: string;
    cellulare?: string;
    email?: string;
    principale: boolean;
    ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
  }>;
  filiali?: Array<{
    id: number;
    nome: string;
    via?: string;
    cap?: string;
    citta?: string;
    provincia?: string;
    paese?: string;
    note?: string;
    attivo: boolean;
  }>;
  documenti?: Array<{
    id: number;
    fileName: string;
    fileUrl: string;
    fileMimeType?: string;
    fileSize?: number;
    etichetta?: string;
    uploadedAt: string;
  }>;
  log?: Array<{
    id: number;
    createdAt: string;
    utenteName: string;
    azione: string;
    dettaglio?: string;
  }>;
}

// ── Componente principale ─────────────────────────────────────────────────
export default function NominativoSchedaPage() {
  const { id } = useParams<{ id: string }>();

  const [nominativo, setNominativo] = useState<Nominativo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAbilitaClienteModal, setShowAbilitaClienteModal] = useState(false);
  const [showAbilitaFornitoreModal, setShowAbilitaFornitoreModal] = useState(false);
  const [abilitando, setAbilitando] = useState(false);
  const [formAbilitaCliente, setFormAbilitaCliente] = useState({
    categoriaCliente: 'AZIENDA_B2B',
    accountId: null as number | null,
    insegnaClienteId: null as number | null,
    agenteNominativoId: null as number | null,
    note: '',
  });
  const [formAbilitaFornitore, setFormAbilitaFornitore] = useState({
    categoriaFornitore: 'FORNITORE_MERCE',
    iban: '',
    banca: '',
    filialeBanca: '',
    note: '',
  });
  const [utentiDisponibili, setUtentiDisponibili] = useState<any[]>([]);
  const [insegneDisponibili, setInsegneDisponibili] = useState<any[]>([]);
  const [agentiDisponibili, setAgentiDisponibili] = useState<any[]>([]);
  const [selectedAccountAbilita, setSelectedAccountAbilita] = useState<any | null>(null);
  const [selectedInsegnaAbilita, setSelectedInsegnaAbilita] = useState<any | null>(null);
  const [selectedAgenteAbilita, setSelectedAgenteAbilita] = useState<any | null>(null);
  const [showAccountModalAbilita, setShowAccountModalAbilita] = useState(false);
  const [showInsegnaModalAbilita, setShowInsegnaModalAbilita] = useState(false);
  const [showAgenteModalAbilita, setShowAgenteModalAbilita] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState({
    ragioneSociale: '',
    nome: '',
    cognome: '',
    partitaIva: '',
    codiceFiscale: '',
    codiceSDI: '',
    pec: '',
    telefono: '',
    emailPrincipale: '',
    sito: '',
    note: '',
  });

  // Blocco cambio società mentre si è nella scheda
  const labelNominativo = nominativo
    ? (nominativo.ragioneSociale ||
       [nominativo.nome, nominativo.cognome].filter(Boolean).join(' ') ||
       `#${nominativo.id}`)
    : 'Nominativo';
  useBloccoRecordContesto({
    recordSocietaId: nominativo?.societa?.id,
    label: nominativo ? `Nominativo ${labelNominativo}` : 'Nominativo',
  });

  useEffect(() => {
    if (id) {
      loadNominativo();
    }
  }, [id]);

  const loadNominativo = async () => {
    if (!id) return;
    try {
      setLoading(true);
      // @api GET /api/nominativi/:id -> {"id":"number","codice":"string","tipoSoggetto":"string","ragioneSociale":"string","nome":"string","cognome":"string","partitaIva":"string","codiceFiscale":"string","isCliente":"boolean","isFornitore":"boolean","cliente":"object","fornitore":"object","indirizzi":"array","email":"array","contatti":"array","filiali":"array","documenti":"array","log":"array"}
      const data = await apiFetch<Nominativo>(`/api/nominativi/${id}`);
      setNominativo(data);
      setError('');
    } catch (err: any) {
      console.error('Errore caricamento nominativo:', err);
      if (err.status === 403) {
        setError('Non hai i permessi per visualizzare questo nominativo');
      } else if (err.status === 404) {
        setError('Nominativo non trovato');
      } else {
        setError(err.message || 'Errore nel caricamento del nominativo');
      }
    } finally {
      setLoading(false);
    }
  };

  const getNomeCompleto = () => {
    if (!nominativo) return '';
    if (nominativo.tipoSoggetto === 'AZIENDA') {
      return nominativo.ragioneSociale || '—';
    }
    return `${nominativo.nome || ''} ${nominativo.cognome || ''}`.trim() || '—';
  };

  const getIniziali = () => {
    const nome = getNomeCompleto();
    const words = nome.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return nome.substring(0, 2).toUpperCase();
  };

  const getCitta = () => {
    if (!nominativo?.indirizzi || nominativo.indirizzi.length === 0) return null;
    const sedeLegale = nominativo.indirizzi.find(i => i.tipo === 'SEDE_LEGALE');
    return sedeLegale?.citta || nominativo.indirizzi[0]?.citta;
  };

  const handleEsporta = () => {
    alert('Funzionalità in arrivo');
  };

  const openAbilitaClienteModal = async () => {
    setFormAbilitaCliente({
      categoriaCliente: 'AZIENDA_B2B',
      accountId: null,
      insegnaClienteId: null,
      agenteNominativoId: null,
      note: '',
    });
    setSelectedAccountAbilita(null);
    setSelectedInsegnaAbilita(null);
    setSelectedAgenteAbilita(null);
    try {
      const [dataUtenti, dataInsegne, dataAgenti] = await Promise.all([
        // @api GET /api/utenti/selezionabili?societaId=N -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        apiFetch<any>(`/api/utenti/selezionabili?societaId=${nominativo!.societa!.id}`),
        apiFetch<any>(`/api/anagrafiche/insegne?societaId=${nominativo!.societa!.id}`),
        apiFetch<any>(`/api/nominativi?categoriaFornitore=AGENTE&societaId=${nominativo!.societa!.id}`),
      ]);
      setUtentiDisponibili(dataUtenti.data ?? []);
      const insegneArr = Array.isArray(dataInsegne) ? dataInsegne : (dataInsegne.insegne ?? dataInsegne.data ?? []);
      setInsegneDisponibili(insegneArr);
      const agentiArr = Array.isArray(dataAgenti) ? dataAgenti : (dataAgenti.data ?? []);
      setAgentiDisponibili(agentiArr);
    } catch (err) {
      console.error('[NominativoSchedaPage][openAbilitaClienteModal]', err);
    }
    setShowAbilitaClienteModal(true);
  };

  const handleAbilitaCliente = async () => {
    if (!nominativo) return;
    setAbilitando(true);
    try {
      // @api POST /api/nominativi/:id/abilita-cliente {"categoriaCliente":"string","accountId":"number","insegnaClienteId":"number","agenteNominativoId":"number","note":"string"} -> {"success":"boolean","message":"string"}
      await apiFetch(`/api/nominativi/${nominativo.id}/abilita-cliente`, {
        method: 'POST',
        body: JSON.stringify({
          categoriaCliente: formAbilitaCliente.categoriaCliente,
          accountId: formAbilitaCliente.accountId,
          insegnaClienteId: formAbilitaCliente.insegnaClienteId,
          agenteNominativoId: formAbilitaCliente.agenteNominativoId,
          note: formAbilitaCliente.note || null,
        }),
      });
      setShowAbilitaClienteModal(false);
      await loadNominativo();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'abilitazione come cliente');
    } finally {
      setAbilitando(false);
    }
  };

  const openAbilitaFornitoreModal = () => {
    setFormAbilitaFornitore({
      categoriaFornitore: 'FORNITORE_MERCE',
      iban: '',
      banca: '',
      filialeBanca: '',
      note: '',
    });
    setShowAbilitaFornitoreModal(true);
  };

  const handleAbilitaFornitore = async () => {
    if (!nominativo) return;
    setAbilitando(true);
    try {
      // @api POST /api/nominativi/:id/abilita-fornitore {"categoriaFornitore":"string","iban":"string","banca":"string","filialeBanca":"string","note":"string"} -> {"success":"boolean","message":"string"}
      await apiFetch(`/api/nominativi/${nominativo.id}/abilita-fornitore`, {
        method: 'POST',
        body: JSON.stringify({
          categoriaFornitore: formAbilitaFornitore.categoriaFornitore,
          iban: formAbilitaFornitore.iban || null,
          banca: formAbilitaFornitore.banca || null,
          filialeBanca: formAbilitaFornitore.filialeBanca || null,
          note: formAbilitaFornitore.note || null,
        }),
      });
      setShowAbilitaFornitoreModal(false);
      await loadNominativo();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'abilitazione come fornitore');
    } finally {
      setAbilitando(false);
    }
  };

  const openEditModal = () => {
    if (!nominativo) return;
    setForm({
      ragioneSociale: nominativo.ragioneSociale || '',
      nome: nominativo.nome || '',
      cognome: nominativo.cognome || '',
      partitaIva: nominativo.partitaIva || '',
      codiceFiscale: nominativo.codiceFiscale || '',
      codiceSDI: nominativo.codiceSDI || '',
      pec: nominativo.pec || '',
      telefono: nominativo.telefono || '',
      emailPrincipale: nominativo.emailPrincipale || '',
      sito: nominativo.sito || '',
      note: nominativo.note || '',
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!nominativo) return;

    // Validazione campi obbligatori
    const errors: Record<string, boolean> = {};
    if (nominativo.tipoSoggetto === 'AZIENDA') {
      if (!form.ragioneSociale.trim()) errors.ragioneSociale = true;
    } else {
      if (!form.nome.trim()) errors.nome = true;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      // @api PUT /api/nominativi/:id {"ragioneSociale":"string","nome":"string","cognome":"string","partitaIva":"string","codiceFiscale":"string","codiceSDI":"string","pec":"string","telefono":"string","emailPrincipale":"string","sito":"string","note":"string"} -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ragioneSociale: form.ragioneSociale || null,
          nome: form.nome || null,
          cognome: form.cognome || null,
          partitaIva: form.partitaIva || null,
          codiceFiscale: form.codiceFiscale || null,
          codiceSDI: form.codiceSDI || null,
          pec: form.pec || null,
          telefono: form.telefono || null,
          emailPrincipale: form.emailPrincipale || null,
          sito: form.sito || null,
          note: form.note || null,
        }),
      });
      setShowEditModal(false);
      loadNominativo();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Condizione di caricamento
  if (loading) {
    return (
      <Layout>
        <div className="bop-nominativo-scheda__loading">
          <p className="bop-text-muted">Caricamento...</p>
        </div>
      </Layout>
    );
  }

  // Errore nel caricamento
  if (error) {
    return (
      <Layout>
        <div className="bop-nominativo-scheda__error-page">
          <h2 className="bop-nominativo-scheda__error-title">⚠️ Errore</h2>
          <p className="bop-nominativo-scheda__error-text">{error}</p>
          <BopBackLink to="/anagrafiche/nominativi" label="Torna ai nominativi" />
        </div>
      </Layout>
    );
  }

  if (!nominativo) {
    return (
      <Layout>
        <div className="bop-nominativo-scheda__loading">
          <p className="bop-text-muted">Nessun dato</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Pulsante Indietro */}
      <BopBackLink to="/anagrafiche/nominativi" label="Torna ai nominativi" />

      {/* Entity Header */}
      <div className="bop-nominativo-scheda__entity-header">
        <div className="bop-nominativo-scheda__avatar">{getIniziali()}</div>
        <div className="bop-nominativo-scheda__entity-info">
          <div className="bop-nominativo-scheda__entity-name">{getNomeCompleto()}</div>
          <div className="bop-nominativo-scheda__badges">
            {/* Badge ruolo */}
            {nominativo.isCliente && (
              <span className="bop-badge bop-badge-info">Cliente</span>
            )}
            {nominativo.isFornitore && (
              <span className="bop-badge bop-badge-warning">Fornitore</span>
            )}

            {/* Badge stato */}
            {nominativo.disattivato ? (
              <span className="bop-badge bop-badge-neutral">Disattivato</span>
            ) : nominativo.bloccato ? (
              <span className="bop-badge bop-badge-warning">Bloccato</span>
            ) : (
              <span className="bop-badge bop-badge-success">Attivo</span>
            )}
          </div>
          <div className="bop-nominativo-scheda__entity-meta">
            <div className="bop-nominativo-scheda__meta-item">
              <strong>Codice:</strong> <span className="bop-nominativo-scheda__mono">{nominativo.codice}</span>
            </div>
            {nominativo.societa && (
              <div className="bop-nominativo-scheda__meta-item">
                <strong>Società:</strong> {nominativo.societa.nome}
              </div>
            )}
            {nominativo.partitaIva && (
              <div className="bop-nominativo-scheda__meta-item">
                <strong>P.IVA:</strong> <span className="bop-nominativo-scheda__mono">{nominativo.partitaIva}</span>
              </div>
            )}
            {nominativo.cliente && (
              <div className="bop-nominativo-scheda__meta-item">
                <strong>Categoria:</strong> {nominativo.cliente.categoriaCliente.replace('_', ' ')}
              </div>
            )}
            {nominativo.cliente?.insegnaCliente && (
              <div className="bop-nominativo-scheda__meta-item">
                <strong>Insegna:</strong> {nominativo.cliente.insegnaCliente.nome}
              </div>
            )}
            {getCitta() && (
              <div className="bop-nominativo-scheda__meta-item">
                <strong>Città:</strong> {getCitta()}
              </div>
            )}
          </div>
        </div>
        <div className="bop-nominativo-scheda__header-actions">
          <button className="bop-btn-primary" onClick={openEditModal}>
            ✏️ Modifica
          </button>
          <button className="bop-btn-secondary bop-nominativo-scheda__btn-export" onClick={handleEsporta}>
            ⬇ Esporta
          </button>
        </div>
      </div>

      {/* Warning/Error Banners */}
      {nominativo.bloccato && !nominativo.disattivato && (
        <div className="bop-nominativo-scheda__warning-banner">
          ⚠️ Nominativo bloccato — non utilizzabile per operazioni commerciali (ordini, preventivi, ecc.)
        </div>
      )}

      {nominativo.disattivato && (
        <div className="bop-nominativo-scheda__error-banner">
          🔴 Nominativo disattivato il{' '}
          {nominativo.dataDisattivazione
            ? new Date(nominativo.dataDisattivazione).toLocaleDateString('it-IT')
            : '(data sconosciuta)'}{' '}
          da {nominativo.utenteDisattivazione || '(utente sconosciuto)'}
        </div>
      )}

      {/* Tabs */}
      <div className="bop-nominativo-scheda__tabs-container">
        <div className="bop-tabs">
          <button className={`bop-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
            Dashboard
          </button>

          <button className={`bop-tab ${activeTab === 'anagrafica' ? 'active' : ''}`} onClick={() => setActiveTab('anagrafica')}>
            Anagrafica
          </button>

          <button
            className={`bop-tab ${activeTab === 'cliente' ? 'active' : ''} ${!nominativo.isCliente ? 'bop-tab--disabled' : ''}`}
            onClick={() => setActiveTab('cliente')}
            title={!nominativo.isCliente ? 'Non abilitato — clicca per attivare' : ''}
          >
            {!nominativo.isCliente && '🔒 '}Cliente
          </button>

          <button
            className={`bop-tab ${activeTab === 'fornitore' ? 'active' : ''} ${!nominativo.isFornitore ? 'bop-tab--disabled' : ''}`}
            onClick={() => setActiveTab('fornitore')}
            title={!nominativo.isFornitore ? 'Non abilitato — clicca per attivare' : ''}
          >
            {!nominativo.isFornitore && '🔒 '}Fornitore
          </button>

          <button className={`bop-tab ${activeTab === 'documenti' ? 'active' : ''}`} onClick={() => setActiveTab('documenti')}>
            Documenti
          </button>

          <button className={`bop-tab ${activeTab === 'log' ? 'active' : ''}`} onClick={() => setActiveTab('log')}>
            Log / Storico
          </button>
        </div>

        <div className="bop-tab-content">
          {activeTab === 'dashboard' && (
            <TabDashboard nominativoId={Number(id)} />
          )}

          {activeTab === 'anagrafica' && (
            <TabAnagrafica nominativo={nominativo} onReload={loadNominativo} />
          )}

          {activeTab === 'cliente' && (
            nominativo.isCliente && nominativo.cliente && nominativo.societa ? (
              <TabCliente
                nominativoId={nominativo.id}
                societaId={nominativo.societa.id}
                cliente={nominativo.cliente}
                email={nominativo.email || []}
                contatti={nominativo.contatti || []}
                filiali={nominativo.filiali || []}
                onReload={loadNominativo}
              />
            ) : (
              <div className="bop-nominativo-scheda__empty-state">
                <div className="bop-nominativo-scheda__empty-icon">👤</div>
                <h3 className="bop-nominativo-scheda__empty-title">Questo nominativo non è ancora un cliente</h3>
                <p className="bop-nominativo-scheda__empty-text">
                  Abilitando il ruolo cliente potrai gestire preventivi, ordini, fatture e scadenzario per questo nominativo.
                </p>
                <button className="bop-btn-primary" onClick={openAbilitaClienteModal}>
                  Abilita come cliente
                </button>
              </div>
            )
          )}

          {activeTab === 'fornitore' && (
            nominativo.isFornitore && nominativo.fornitore && nominativo.societa ? (
              <TabFornitore
                nominativoId={nominativo.id}
                societaId={nominativo.societa.id}
                fornitore={nominativo.fornitore}
                email={nominativo.email || []}
                contatti={nominativo.contatti || []}
                onReload={loadNominativo}
              />
            ) : (
              <div className="bop-nominativo-scheda__empty-state">
                <div className="bop-nominativo-scheda__empty-icon">🏭</div>
                <h3 className="bop-nominativo-scheda__empty-title">Questo nominativo non è ancora un fornitore</h3>
                <p className="bop-nominativo-scheda__empty-text">
                  Abilitando il ruolo fornitore potrai gestire ordini passivi, fatture e scadenzario per questo nominativo.
                </p>
                <button className="bop-btn-primary" onClick={openAbilitaFornitoreModal}>
                  Abilita come fornitore
                </button>
              </div>
            )
          )}

          {activeTab === 'documenti' && (
            <TabDocumenti nominativoId={nominativo.id} documenti={nominativo.documenti || []} onReload={loadNominativo} />
          )}

          {activeTab === 'log' && (
            <TabLog key={nominativo.log?.length || 0} log={nominativo.log || []} />
          )}
        </div>
      </div>

      {/* Modal Modifica Dati Base */}
      {showEditModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-nominativo-scheda__modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="bop-nominativo-scheda__modal-header">Modifica Dati Base Nominativo</h3>

            {nominativo.tipoSoggetto === 'AZIENDA' ? (
              <div className="bop-form-group">
                <label className="bop-label">Ragione Sociale *</label>
                <input
                  className={`bop-input ${formErrors.ragioneSociale ? 'bop-input--error' : ''}`}
                  type="text"
                  value={form.ragioneSociale}
                  onChange={(e) => setForm(prev => ({ ...prev, ragioneSociale: e.target.value }))}
                  disabled={saving}
                />
                {formErrors.ragioneSociale && (
                  <span className="bop-nominativo-scheda__field-error">
                    Campo obbligatorio
                  </span>
                )}
              </div>
            ) : (
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input
                    className={`bop-input ${formErrors.nome ? 'bop-input--error' : ''}`}
                    type="text"
                    value={form.nome}
                    onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
                    disabled={saving}
                  />
                  {formErrors.nome && (
                    <span className="bop-nominativo-scheda__field-error">
                      Campo obbligatorio
                    </span>
                  )}
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome</label>
                  <input
                    className="bop-input"
                    type="text"
                    value={form.cognome}
                    onChange={(e) => setForm(prev => ({ ...prev, cognome: e.target.value }))}
                    disabled={saving}
                  />
                </div>
              </div>
            )}

            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Partita IVA</label>
                <input
                  className="bop-input"
                  type="text"
                  value={form.partitaIva}
                  onChange={(e) => setForm(prev => ({ ...prev, partitaIva: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Codice Fiscale</label>
                <input
                  className="bop-input"
                  type="text"
                  value={form.codiceFiscale}
                  onChange={(e) => setForm(prev => ({ ...prev, codiceFiscale: e.target.value }))}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Codice SDI</label>
                <input
                  className="bop-input"
                  type="text"
                  value={form.codiceSDI}
                  onChange={(e) => setForm(prev => ({ ...prev, codiceSDI: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">PEC</label>
                <input
                  className="bop-input"
                  type="email"
                  value={form.pec}
                  onChange={(e) => setForm(prev => ({ ...prev, pec: e.target.value }))}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Telefono</label>
                <input
                  className="bop-input"
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setForm(prev => ({ ...prev, telefono: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email Principale</label>
                <input
                  className="bop-input"
                  type="email"
                  value={form.emailPrincipale}
                  onChange={(e) => setForm(prev => ({ ...prev, emailPrincipale: e.target.value }))}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Sito Web</label>
              <input
                className="bop-input"
                type="url"
                value={form.sito}
                onChange={(e) => setForm(prev => ({ ...prev, sito: e.target.value }))}
                placeholder="https://"
                disabled={saving}
              />
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <textarea
                className="bop-input bop-nominativo-scheda__textarea"
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note aggiuntive sul nominativo..."
                disabled={saving}
              />
            </div>

            <div className="bop-nominativo-scheda__modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Abilita Cliente */}
      {showAbilitaClienteModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-nominativo-scheda__modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="bop-nominativo-scheda__modal-header">Abilita come Cliente</h3>

            <div className="bop-form-group">
              <label className="bop-label">Categoria Cliente *</label>
              <select
                className="bop-input"
                value={formAbilitaCliente.categoriaCliente}
                onChange={(e) => setFormAbilitaCliente(prev => ({ ...prev, categoriaCliente: e.target.value }))}
                disabled={abilitando}
              >
                <option value="CATENA_GDO">Catena GDO</option>
                <option value="AZIENDA_B2B">Azienda B2B</option>
                <option value="PRIVATO_B2C">Privato B2C</option>
                <option value="AGENTE">Agente</option>
              </select>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Account Manager</label>
              <div className="bop-nominativo-scheda__lookup-row">
                <div className="bop-nominativo-scheda__lookup-display">
                  {selectedAccountAbilita
                    ? `${selectedAccountAbilita.nome || ''} ${selectedAccountAbilita.cognome || ''}`.trim() || selectedAccountAbilita.email
                    : <span className="bop-text-muted">Nessuno selezionato</span>}
                </div>
                <button type="button" className="bop-btn-secondary" onClick={() => setShowAccountModalAbilita(true)} disabled={abilitando}>
                  Cerca / Cambia
                </button>
                {selectedAccountAbilita && (
                  <button type="button" className="bop-btn-secondary bop-nominativo-scheda__btn-remove" onClick={() => { setSelectedAccountAbilita(null); setFormAbilitaCliente(prev => ({ ...prev, accountId: null })); }} disabled={abilitando}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Insegna</label>
              <div className="bop-nominativo-scheda__lookup-row">
                <div className="bop-nominativo-scheda__lookup-display">
                  {selectedInsegnaAbilita
                    ? <><span className="bop-nominativo-scheda__mono bop-nominativo-scheda__mono--spaced">{selectedInsegnaAbilita.codice}</span>— {selectedInsegnaAbilita.nome}</>
                    : <span className="bop-text-muted">Nessuna selezionata</span>}
                </div>
                <button type="button" className="bop-btn-secondary" onClick={() => setShowInsegnaModalAbilita(true)} disabled={abilitando}>
                  Cerca / Cambia
                </button>
                {selectedInsegnaAbilita && (
                  <button type="button" className="bop-btn-secondary bop-nominativo-scheda__btn-remove" onClick={() => { setSelectedInsegnaAbilita(null); setFormAbilitaCliente(prev => ({ ...prev, insegnaClienteId: null })); }} disabled={abilitando}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Agente Assegnato</label>
              <div className="bop-nominativo-scheda__lookup-row">
                <div className="bop-nominativo-scheda__lookup-display">
                  {selectedAgenteAbilita
                    ? <><span className="bop-nominativo-scheda__mono bop-nominativo-scheda__mono--spaced">{selectedAgenteAbilita.codice}</span>— {selectedAgenteAbilita.ragioneSociale || selectedAgenteAbilita.nome}</>
                    : <span className="bop-text-muted">Nessuno selezionato</span>}
                </div>
                <button type="button" className="bop-btn-secondary" onClick={() => setShowAgenteModalAbilita(true)} disabled={abilitando}>
                  Cerca / Cambia
                </button>
                {selectedAgenteAbilita && (
                  <button type="button" className="bop-btn-secondary bop-nominativo-scheda__btn-remove" onClick={() => { setSelectedAgenteAbilita(null); setFormAbilitaCliente(prev => ({ ...prev, agenteNominativoId: null })); }} disabled={abilitando}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <textarea
                className="bop-input bop-nominativo-scheda__textarea"
                value={formAbilitaCliente.note}
                onChange={(e) => setFormAbilitaCliente(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note sul ruolo cliente..."
                disabled={abilitando}
              />
            </div>

            <div className="bop-nominativo-scheda__modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowAbilitaClienteModal(false)} disabled={abilitando}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={handleAbilitaCliente} disabled={abilitando}>
                {abilitando ? 'Abilitazione...' : 'Abilita come Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modali di ricerca per abilita-cliente */}
      {showAccountModalAbilita && (
        <ModaleCercaRecord
          titolo="Seleziona Account Manager"
          colonne={[
            { key: 'nome', label: 'Nome e Cognome' },
          ]}
          dati={utentiDisponibili}
          campoRicerca={['nome']}
          onSeleziona={(record) => {
            setSelectedAccountAbilita(record);
            setFormAbilitaCliente(prev => ({ ...prev, accountId: record.id }));
            setShowAccountModalAbilita(false);
          }}
          onChiudi={() => setShowAccountModalAbilita(false)}
        />
      )}

      {showInsegnaModalAbilita && (
        <ModaleCercaRecord
          titolo="Seleziona Insegna"
          colonne={[
            { key: 'codice', label: 'Codice', mono: true },
            { key: 'nome', label: 'Nome Insegna' },
          ]}
          dati={insegneDisponibili}
          campoRicerca={['codice', 'nome']}
          onSeleziona={(record) => {
            setSelectedInsegnaAbilita(record);
            setFormAbilitaCliente(prev => ({ ...prev, insegnaClienteId: record.id }));
            setShowInsegnaModalAbilita(false);
          }}
          onChiudi={() => setShowInsegnaModalAbilita(false)}
        />
      )}

      {showAgenteModalAbilita && (
        <ModaleCercaRecord
          titolo="Seleziona Agente"
          colonne={[
            { key: 'codice', label: 'Codice', mono: true },
            { key: 'ragioneSociale', label: 'Ragione Sociale / Nome' },
            { key: 'partitaIva', label: 'P.IVA', mono: true },
            { key: 'citta', label: 'Città' },
          ]}
          dati={agentiDisponibili.map(a => ({
            ...a,
            ragioneSociale: a.ragioneSociale || a.nome || '—',
            citta: a.indirizzi?.find((i: any) => i.tipo === 'SEDE_LEGALE')?.citta || '—',
          }))}
          campoRicerca={['codice', 'ragioneSociale', 'nome']}
          onSeleziona={(record) => {
            setSelectedAgenteAbilita(record);
            setFormAbilitaCliente(prev => ({ ...prev, agenteNominativoId: record.id }));
            setShowAgenteModalAbilita(false);
          }}
          onChiudi={() => setShowAgenteModalAbilita(false)}
        />
      )}

      {/* Modal Abilita Fornitore */}
      {showAbilitaFornitoreModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-nominativo-scheda__modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="bop-nominativo-scheda__modal-header">Abilita come Fornitore</h3>

            <div className="bop-form-group">
              <label className="bop-label">Categoria Fornitore *</label>
              <select
                className="bop-input"
                value={formAbilitaFornitore.categoriaFornitore}
                onChange={(e) => setFormAbilitaFornitore(prev => ({ ...prev, categoriaFornitore: e.target.value }))}
                disabled={abilitando}
              >
                <option value="FORNITORE_MERCE">Fornitore Merce</option>
                <option value="SUBAPPALTATORE">Subappaltatore</option>
                <option value="FORNITORE_SERVIZI">Fornitore Servizi</option>
                <option value="AGENTE">Agente</option>
              </select>
            </div>

            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">IBAN</label>
                <input
                  className="bop-input"
                  type="text"
                  value={formAbilitaFornitore.iban}
                  onChange={(e) => setFormAbilitaFornitore(prev => ({ ...prev, iban: e.target.value }))}
                  placeholder="IT00A0000000000000000000000"
                  disabled={abilitando}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Banca</label>
                <input
                  className="bop-input"
                  type="text"
                  value={formAbilitaFornitore.banca}
                  onChange={(e) => setFormAbilitaFornitore(prev => ({ ...prev, banca: e.target.value }))}
                  placeholder="es. Intesa Sanpaolo"
                  disabled={abilitando}
                />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Filiale Banca</label>
              <input
                className="bop-input"
                type="text"
                value={formAbilitaFornitore.filialeBanca}
                onChange={(e) => setFormAbilitaFornitore(prev => ({ ...prev, filialeBanca: e.target.value }))}
                placeholder="es. Milano Porta Nuova"
                disabled={abilitando}
              />
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <textarea
                className="bop-input bop-nominativo-scheda__textarea"
                value={formAbilitaFornitore.note}
                onChange={(e) => setFormAbilitaFornitore(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note sul ruolo fornitore..."
                disabled={abilitando}
              />
            </div>

            <div className="bop-nominativo-scheda__modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowAbilitaFornitoreModal(false)} disabled={abilitando}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={handleAbilitaFornitore} disabled={abilitando}>
                {abilitando ? 'Abilitazione...' : 'Abilita come Fornitore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
